import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Onboarding from '../components/Onboarding';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Button,
    CircularProgress,
    useTheme,
    Card,
    CardContent,
    LinearProgress,
    Chip,
    TextField,
    Tooltip,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerIcon from '@mui/icons-material/Timer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import StarIcon from '@mui/icons-material/Star';
import ReadingAnalytics from '../components/analytics/ReadingAnalytics';
import SubscriptionStatus from '../components/SubscriptionStatus';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend
} from 'recharts';
import api from '../services/api';
import ChallengeFriendDialog from '../components/flex/ChallengeFriendDialog';
import { trackFlexEvent, FLEX_EVENTS } from '../utils/flexAnalytics';
import { trackServerEvent } from '../utils/analytics';

const StatCard = ({ icon, title, value, subtitle, color }) => {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                        backgroundColor: `${color}20`,
                        borderRadius: '50%',
                        p: 1,
                        mr: 2
                    }}>
                        {icon}
                    </Box>
                    <Typography variant="h6" color="textSecondary">
                        {title}
                    </Typography>
                </Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                    {value}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {subtitle}
                </Typography>
            </CardContent>
        </Card>
    );
};

const ProgressChart = ({ data }) => {
    const theme = useTheme();
    return (
        <Paper sx={{ p: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>Reading Progress Details</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                            Reading Speed Progress
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="body1" sx={{ flexGrow: 1, fontWeight: 500 }}>
                                Current Average: {data?.averageWPM || 0} WPM
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                                Best: {data?.bestWPM || 0} WPM
                            </Typography>
                        </Box>
                        <Box sx={{ position: 'relative', mt: 1, mb: 1 }}>
                            <LinearProgress 
                                variant="determinate" 
                                value={Math.min((data?.averageWPM || 0) / 25, 100)} 
                                sx={{ 
                                    height: 12, 
                                    borderRadius: 6,
                                    backgroundColor: `${theme.palette.primary.main}15`,
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 6,
                                        backgroundColor: theme.palette.primary.main,
                                        backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                                    }
                                }}
                            />
                            {/* Add markers for reading speed milestones */}
                            <Box sx={{ 
                                position: 'absolute', 
                                top: -2, 
                                left: '25%', 
                                width: 2, 
                                height: 16, 
                                backgroundColor: theme.palette.grey[400],
                                opacity: 0.7
                            }} />
                            <Box sx={{ 
                                position: 'absolute', 
                                top: -2, 
                                left: '50%', 
                                width: 2, 
                                height: 16, 
                                backgroundColor: theme.palette.grey[400],
                                opacity: 0.7
                            }} />
                            <Box sx={{ 
                                position: 'absolute', 
                                top: -2, 
                                left: '75%', 
                                width: 2, 
                                height: 16, 
                                backgroundColor: theme.palette.grey[400],
                                opacity: 0.7
                            }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 0.5 }}>
                            <Typography variant="caption" color="textSecondary">0</Typography>
                            <Typography variant="caption" color="textSecondary">625</Typography>
                            <Typography variant="caption" color="textSecondary">1250</Typography>
                            <Typography variant="caption" color="textSecondary">1875</Typography>
                            <Typography variant="caption" color="textSecondary">2500</Typography>
                        </Box>
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                            Comprehension Rate
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="body1" sx={{ flexGrow: 1, fontWeight: 500 }}>
                                Average Score: {data?.averageQuizScore || 0}%
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                                Best: {data?.bestQuizScore || 0}%
                            </Typography>
                        </Box>
                        <Box sx={{ position: 'relative', mt: 1, mb: 1 }}>
                            <LinearProgress 
                                variant="determinate" 
                                value={data?.averageQuizScore || 0} 
                                sx={{ 
                                    height: 12, 
                                    borderRadius: 6,
                                    backgroundColor: `${theme.palette.success.main}15`,
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 6,
                                        backgroundColor: theme.palette.success.main,
                                        backgroundImage: `linear-gradient(90deg, ${theme.palette.success.light}, ${theme.palette.success.main})`,
                                    }
                                }}
                            />
                            {/* Add markers for comprehension milestones */}
                            <Box sx={{ 
                                position: 'absolute', 
                                top: -2, 
                                left: '25%', 
                                width: 2, 
                                height: 16, 
                                backgroundColor: theme.palette.grey[400],
                                opacity: 0.7
                            }} />
                            <Box sx={{ 
                                position: 'absolute', 
                                top: -2, 
                                left: '50%', 
                                width: 2, 
                                height: 16, 
                                backgroundColor: theme.palette.grey[400],
                                opacity: 0.7
                            }} />
                            <Box sx={{ 
                                position: 'absolute', 
                                top: -2, 
                                left: '75%', 
                                width: 2, 
                                height: 16, 
                                backgroundColor: theme.palette.grey[400],
                                opacity: 0.7
                            }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 0.5 }}>
                            <Typography variant="caption" color="textSecondary">0%</Typography>
                            <Typography variant="caption" color="textSecondary">25%</Typography>
                            <Typography variant="caption" color="textSecondary">50%</Typography>
                            <Typography variant="caption" color="textSecondary">75%</Typography>
                            <Typography variant="caption" color="textSecondary">100%</Typography>
                        </Box>
                    </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <Box sx={{ 
                        mb: 3, 
                        p: 2, 
                        borderRadius: 2, 
                        backgroundColor: theme.palette.background.default,
                        boxShadow: 'inset 0 0 5px rgba(0,0,0,0.05)'
                    }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                            Quiz Performance
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            mb: 1,
                            mt: 2
                        }}>
                            <Box sx={{ 
                                textAlign: 'center', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center'
                            }}>
                                <Box sx={{ 
                                    width: 60, 
                                    height: 60, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${theme.palette.primary.main}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1
                                }}>
                                    <Typography variant="h5" color="primary.main" sx={{ fontWeight: 600 }}>
                                        {data?.quizzesPassed || 0}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    Quizzes Passed
                                </Typography>
                            </Box>
                            
                            <Box sx={{ 
                                textAlign: 'center', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center'
                            }}>
                                <Box sx={{ 
                                    width: 60, 
                                    height: 60, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${theme.palette.grey[500]}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1
                                }}>
                                    <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        {data?.totalQuizzesTaken || 0}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    Total Quizzes
                                </Typography>
                            </Box>
                            
                            <Box sx={{ 
                                textAlign: 'center', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center'
                            }}>
                                <Box sx={{ 
                                    width: 60, 
                                    height: 60, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${theme.palette.success.main}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1
                                }}>
                                    <Typography variant="h5" color="success.main" sx={{ fontWeight: 600 }}>
                                        {data?.totalQuizzesTaken ? 
                                            Math.round((data.quizzesPassed / data.totalQuizzesTaken) * 100) : 
                                            0}%
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    Success Rate
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ 
                        p: 2, 
                        borderRadius: 2, 
                        backgroundColor: theme.palette.background.default,
                        boxShadow: 'inset 0 0 5px rgba(0,0,0,0.05)'
                    }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                            Reading Activity
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-around', 
                            mb: 1,
                            mt: 2
                        }}>
                            <Box sx={{ 
                                textAlign: 'center', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center'
                            }}>
                                <Box sx={{ 
                                    width: 70, 
                                    height: 70, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${theme.palette.primary.main}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1,
                                    position: 'relative'
                                }}>
                                    <MenuBookIcon 
                                        sx={{ 
                                            color: theme.palette.primary.main,
                                            opacity: 0.2,
                                            fontSize: 30,
                                            position: 'absolute'
                                        }} 
                                    />
                                    <Typography variant="h5" color="primary.main" sx={{ fontWeight: 600, zIndex: 1 }}>
                                        {data?.articlesRead || 0}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    Articles Read
                                </Typography>
                            </Box>
                            
                            <Box sx={{ 
                                textAlign: 'center', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center'
                            }}>
                                <Box sx={{ 
                                    width: 70, 
                                    height: 70, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${theme.palette.info.main}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1,
                                    position: 'relative'
                                }}>
                                    <TimerIcon 
                                        sx={{ 
                                            color: theme.palette.info.main,
                                            opacity: 0.2,
                                            fontSize: 30,
                                            position: 'absolute'
                                        }} 
                                    />
                                    <Typography variant="h5" color="info.main" sx={{ fontWeight: 600, zIndex: 1 }}>
                                        {Math.round((data?.totalReadingTime || 0) / 60)}h
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    Total Reading Time
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

// XP Level Progress Bar
const XPBar = ({ xp, level, title, xpForCurrentLevel, xpForNextLevel, streakMultiplier }) => {
    const theme = useTheme();
    const currentLevelXP = xp - (xpForCurrentLevel || 0);
    const levelRange = (xpForNextLevel || 100) - (xpForCurrentLevel || 0);
    const progressPct = levelRange > 0 ? Math.min(100, (currentLevelXP / levelRange) * 100) : 0;

    return (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`, color: '#fff' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: '#FFD700', fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold">Level {level}</Typography>
                    <Chip label={title} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>{xp.toLocaleString()} XP</Typography>
                    {streakMultiplier > 1 && (
                        <Chip icon={<WhatshotIcon sx={{ color: '#FF9800 !important' }} />}
                            label={`${streakMultiplier.toFixed(1)}x XP`} size="small"
                            sx={{ backgroundColor: 'rgba(255,152,0,0.2)', color: '#FFD700', fontWeight: 600, mt: 0.5 }} />
                    )}
                </Box>
            </Box>
            <LinearProgress variant="determinate" value={progressPct}
                sx={{ height: 10, borderRadius: 5, backgroundColor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': { borderRadius: 5, backgroundColor: '#FFD700' } }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Lvl {level}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{currentLevelXP} / {levelRange} XP to Level {level + 1}</Typography>
            </Box>
        </Paper>
    );
};

// Daily Goals Progress
const DailyGoals = ({ dailyGoal, todayProgress }) => {
    const theme = useTheme();
    const goals = [
        { label: 'Words', current: todayProgress?.wordsRead || 0, target: dailyGoal?.wordsTarget || 5000, color: theme.palette.primary.main, format: v => v.toLocaleString() },
        { label: 'Articles', current: todayProgress?.articlesRead || 0, target: dailyGoal?.articlesTarget || 3, color: theme.palette.success.main, format: v => v },
        { label: 'Minutes', current: todayProgress?.minutesRead || 0, target: dailyGoal?.minutesTarget || 30, color: theme.palette.info.main, format: v => v }
    ];
    const allComplete = goals.every(g => g.current >= g.target);

    return (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: allComplete ? `2px solid ${theme.palette.success.main}` : 'none' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>Today's Goals</Typography>
                {allComplete && <Chip label="All Complete!" color="success" size="small" icon={<EmojiEventsIcon />} />}
            </Box>
            <Grid container spacing={2}>
                {goals.map(g => {
                    const pct = Math.min(100, g.target > 0 ? (g.current / g.target) * 100 : 0);
                    return (
                        <Grid item xs={4} key={g.label}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>{g.label}</Typography>
                                <Typography variant="h6" fontWeight="bold" color={pct >= 100 ? 'success.main' : 'text.primary'}>
                                    {g.format(g.current)} <Typography component="span" variant="body2" color="text.secondary">/ {g.format(g.target)}</Typography>
                                </Typography>
                                <LinearProgress variant="determinate" value={pct}
                                    sx={{ height: 6, borderRadius: 3, mt: 1, backgroundColor: `${g.color}20`,
                                        '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: g.color } }} />
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
        </Paper>
    );
};

// Weekly Trend Chart
const TrendChart = ({ weeklyHistory }) => {
    const theme = useTheme();
    if (!weeklyHistory || weeklyHistory.length < 2) {
        return (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>Weekly Trends</Typography>
                <Typography color="text.secondary">Keep reading to see your improvement trends here!</Typography>
            </Paper>
        );
    }

    const data = weeklyHistory.map(w => ({
        week: w.week?.replace(/^\d{4}-/, '') || '',
        'Avg WPM': w.avgWPM || 0,
        'Comprehension %': w.avgComprehension || 0,
        'Articles': w.articlesRead || 0
    }));

    return (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>Weekly Trends</Typography>
            <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <RechartsTooltip />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="Avg WPM" stroke={theme.palette.primary.main} strokeWidth={2} dot={{ r: 4 }} />
                    <Line yAxisId="right" type="monotone" dataKey="Comprehension %" stroke={theme.palette.success.main} strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
            </ResponsiveContainer>
        </Paper>
    );
};

// Next Badge to Earn
const NextBadge = ({ badges }) => {
    const theme = useTheme();
    if (!badges) return null;

    // Find the unearned badge closest to 100% progress
    let best = null;
    Object.entries(badges).forEach(([cat, catBadges]) => {
        Object.entries(catBadges).forEach(([id, badge]) => {
            if (!badge.earned && badge.progress > 0) {
                if (!best || badge.progress > best.progress) {
                    best = { ...badge, category: cat, id };
                }
            }
        });
    });

    if (!best) return null;

    return (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>Next Badge</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: 40 }}>{best.icon}</Typography>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">{best.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{best.description}</Typography>
                    <LinearProgress variant="determinate" value={best.progress}
                        sx={{ height: 8, borderRadius: 4, mt: 1,
                            '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: theme.palette.warning.main } }} />
                    <Typography variant="caption" color="text.secondary">{best.progress}% complete</Typography>
                </Box>
            </Box>
        </Paper>
    );
};

const Dashboard = () => {
    const theme = useTheme();
    const { user, refreshUserData } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [onboardingChecked, setOnboardingChecked] = useState(false);
    const [stats, setStats] = useState({
        currentSpeed: 0,
        averageComprehension: 0,
        practiceStreak: 0,
        totalPracticeTime: 0,
        readingStats: {
            articlesRead: 0,
            totalQuizzesTaken: 0,
            quizzesPassed: 0,
            averageQuizScore: 0,
            bestQuizScore: 0,
            averageWPM: 0,
            bestWPM: 0,
            totalReadingTime: 0,
            totalWordsRead: 0
        }
    });
    const [readingHistory, setReadingHistory] = useState([]);
    const [affiliateStats, setAffiliateStats] = useState(null);
    const [affiliateLoading, setAffiliateLoading] = useState(true);
    const [linkCopied, setLinkCopied] = useState(false);
    const [userBadges, setUserBadges] = useState(null);
    const [loginNotification, setLoginNotification] = useState(null);
    const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
    
    // Define the ref at the component level, not inside useEffect
    const hasFetched = useRef(false);
    
    // Function to handle copying affiliate link to clipboard
    const handleCopyAffiliateLink = () => {
        if (user) {
            const affiliateLink = `${window.location.origin}?ref=${user.username}`;
            navigator.clipboard.writeText(affiliateLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        }
    };
    
    // Render the affiliate analytics section
    const renderAffiliateSection = () => {
        if (affiliateLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={30} />
                </Box>
            );
        }

        return (
            <>
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', position: 'relative' }}>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Your Affiliate Link
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <TextField
                                    value={`${window.location.origin}?ref=${user?.username}`}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <Tooltip title={linkCopied ? "Copied!" : "Copy to clipboard"}>
                                                <IconButton 
                                                    edge="end" 
                                                    onClick={handleCopyAffiliateLink}
                                                    color={linkCopied ? "success" : "default"}
                                                >
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ),
                                    }}
                                />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                Share this link with friends to earn $3.00/month per referral
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{
                                    backgroundColor: `${theme.palette.success.main}20`,
                                    borderRadius: '50%',
                                    p: 1,
                                    mr: 2
                                }}>
                                    <MonetizationOnIcon sx={{ color: theme.palette.success.main }} />
                                </Box>
                                <Typography variant="h6" color="textSecondary">
                                    Earnings
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ mb: 1 }}>
                                ${(affiliateStats?.earnings?.total || 0).toFixed(2)}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Direct earnings:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    ${(affiliateStats?.earnings?.direct || 0).toFixed(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="textSecondary">
                                    Second-level earnings:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    ${(affiliateStats?.earnings?.secondLevel || 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{
                                    backgroundColor: `${theme.palette.info.main}20`,
                                    borderRadius: '50%',
                                    p: 1,
                                    mr: 2
                                }}>
                                    <PeopleAltIcon sx={{ color: theme.palette.info.main }} />
                                </Box>
                                <Typography variant="h6" color="textSecondary">
                                    Referrals
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ mb: 1 }}>
                                {affiliateStats?.referrals?.length || 0}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Direct referrals:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {affiliateStats?.referrals?.filter(ref => ref.level === 1)?.length || 0}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="textSecondary">
                                    Second-level referrals:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {affiliateStats?.referrals?.filter(ref => ref.level === 2)?.length || 0}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button 
                            component={Link} 
                            to="/affiliate" 
                            color="primary"
                            endIcon={<LinkIcon />}
                        >
                            View detailed affiliate stats
                        </Button>
                    </Box>
                </Grid>
            </>
        );
    };
    
    useEffect(() => {
        if (user) trackServerEvent('dashboard_viewed');
    }, [user]);

    useEffect(() => {
        const fetchStats = async () => {
            // Only fetch if we have a user and the component is mounted
            if (!user) return;
            
            try {
                console.log('Fetching stats...');
                
                // Add loading state for each data type
                setLoading(true);
                
                try {
                    const statsResponse = await api.get('/usage/stats');
                    
                    const d = statsResponse.data;
                    setStats(prevStats => ({
                        ...prevStats,
                        currentSpeed: d.averageWPM || 0,
                        averageComprehension: d.averageQuizScore || 0,
                        practiceStreak: d.practiceStreak || 0,
                        totalPracticeTime: d.totalReadingTime || 0,
                        readingStats: {
                            ...d,
                            articlesRead: d.articlesRead || 0,
                            totalQuizzesTaken: d.totalQuizzesTaken || 0,
                            quizzesPassed: d.quizzesPassed || 0,
                            averageQuizScore: d.averageQuizScore || 0,
                            bestQuizScore: d.bestQuizScore || 0,
                            averageWPM: d.averageWPM || 0,
                            bestWPM: d.bestWPM || 0,
                            totalReadingTime: d.totalReadingTime || 0,
                            totalWordsRead: d.totalWordsRead || 0,
                            xp: d.xp || 0,
                            level: d.level || 1,
                            title: d.title || 'Beginner Reader',
                            xpForNextLevel: d.xpForNextLevel || 100,
                            xpForCurrentLevel: d.xpForCurrentLevel || 0,
                            streakMultiplier: d.streakMultiplier || 1,
                            dailyGoal: d.dailyGoal,
                            todayProgress: d.todayProgress,
                            weeklyHistory: d.weeklyHistory || []
                        }
                    }));
                } catch (statsError) {
                    console.error('Failed to fetch stats:', statsError.response || statsError);
                    // Don't set loading to false here, continue with history fetch
                }
                
                try {
                    const historyResponse = await api.get('/usage/history');
                    
                    // Ensure the history data is properly formatted
                    if (Array.isArray(historyResponse.data)) {
                        setReadingHistory(historyResponse.data);
                    } else {
                        console.error('History response is not an array:', historyResponse.data);
                        setReadingHistory([]);
                    }
                } catch (historyError) {
                    console.error('Failed to fetch history:', historyError.response || historyError);
                    setReadingHistory([]);
                }
                
                setLoading(false);
            } catch (error) {
                console.error('Failed to fetch data:', error.response || error);
                setLoading(false);
            }
        };

        const fetchAffiliateData = async () => {
            if (!user) return;
            
            setAffiliateLoading(true);
            try {
                const response = await api.get('/affiliate/stats');
                setAffiliateStats(response.data);
            } catch (error) {
                console.error('Failed to fetch affiliate data:', error);
                setAffiliateStats({
                    earnings: { total: 0 },
                    referrals: []
                });
            } finally {
                setAffiliateLoading(false);
            }
        };

        const claimDailyLogin = async () => {
            try {
                const res = await api.post('/usage/daily-login');
                if (!res.data.alreadyClaimed && res.data.xpGained > 0) {
                    setLoginNotification({ xp: res.data.xpGained, streak: res.data.loginStreak });
                }
            } catch (err) {
                // Non-critical, silently ignore
            }
        };

        const fetchBadges = async () => {
            try {
                const res = await api.get('/users/badges');
                if (res.data.badges) setUserBadges(res.data.badges);
            } catch (err) {
                // Non-critical
            }
        };

        if (user && !hasFetched.current) {
            hasFetched.current = true;
            fetchStats();
            fetchAffiliateData();
            claimDailyLogin();
            fetchBadges();

            // Check if onboarding should be shown
            api.get('/activation/plan')
                .then(res => {
                    if (res.data.shouldShowOnboarding) {
                        setShowOnboarding(true);
                    }
                })
                .catch(() => {})
                .finally(() => setOnboardingChecked(true));
        }
        
        // Clean-up function to reset the ref when the component unmounts
        return () => {
            hasFetched.current = false;
        };
    }, [user]); // Only depend on user

    if (loading || !onboardingChecked) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (showOnboarding) {
        return (
            <Onboarding
                onSkip={() => setShowOnboarding(false)}
                onComplete={() => setShowOnboarding(false)}
            />
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Daily Login XP Notification */}
            <Snackbar open={loginNotification !== null} autoHideDuration={4000}
                onClose={() => setLoginNotification(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="success" onClose={() => setLoginNotification(null)} sx={{ width: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Daily Login Bonus! +{loginNotification?.xp} XP</Typography>
                    <Typography variant="body2">Login streak: {loginNotification?.streak} day{loginNotification?.streak > 1 ? 's' : ''}</Typography>
                </Alert>
            </Snackbar>

            {/* Welcome Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Welcome back, {user?.profile?.displayName || 'Reader'}!
                </Typography>
                <Typography color="textSecondary">
                    Your reading journey continues. Let's improve your skills today.
                </Typography>
            </Box>

            {/* XP Level Bar */}
            <XPBar
                xp={stats.readingStats?.xp || 0}
                level={stats.readingStats?.level || 1}
                title={stats.readingStats?.title || 'Beginner Reader'}
                xpForCurrentLevel={stats.readingStats?.xpForCurrentLevel || 0}
                xpForNextLevel={stats.readingStats?.xpForNextLevel || 100}
                streakMultiplier={stats.readingStats?.streakMultiplier || 1}
            />

            {/* Daily Goals */}
            <DailyGoals
                dailyGoal={stats.readingStats?.dailyGoal}
                todayProgress={stats.readingStats?.todayProgress}
            />

            {/* Subscription Status */}
            <SubscriptionStatus />

            {/* Quick Action Buttons */}
            <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => navigate('/reader')}
                    sx={{
                        py: 2,
                        px: 4,
                        borderRadius: 2,
                        backgroundColor: theme.palette.success.main,
                        '&:hover': {
                            backgroundColor: theme.palette.success.dark,
                        }
                    }}
                >
                    Start Today's Training
                </Button>
                <Button
                    variant="outlined"
                    size="large"
                    startIcon={<EmojiEventsIcon />}
                    onClick={() => {
                        setChallengeDialogOpen(true);
                        trackFlexEvent(FLEX_EVENTS.CHALLENGE_CREATED, { action: 'dialog_opened' });
                    }}
                    sx={{
                        py: 2,
                        px: 4,
                        borderRadius: 2,
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: `${theme.palette.primary.main}10`,
                            borderColor: theme.palette.primary.dark,
                        }
                    }}
                >
                    Challenge a Friend
                </Button>
            </Box>

            <ChallengeFriendDialog
                open={challengeDialogOpen}
                onClose={() => setChallengeDialogOpen(false)}
            />

            {/* Stats Grid */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<SpeedIcon sx={{ color: theme.palette.primary.main }} />}
                        title="Reading Speed"
                        value={`${stats.currentSpeed} WPM`}
                        subtitle="Current reading speed"
                        color={theme.palette.primary.main}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<TrendingUpIcon sx={{ color: theme.palette.success.main }} />}
                        title="Comprehension"
                        value={`${stats.averageComprehension}%`}
                        subtitle="Average comprehension rate"
                        color={theme.palette.success.main}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<EmojiEventsIcon sx={{ color: theme.palette.warning.main }} />}
                        title="Streak"
                        value={`${stats.practiceStreak} Days`}
                        subtitle={stats.readingStats?.streakMultiplier > 1 
                            ? `${stats.readingStats.streakMultiplier.toFixed(1)}x XP multiplier` 
                            : 'Current practice streak'}
                        color={theme.palette.warning.main}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<MenuBookIcon sx={{ color: theme.palette.info.main }} />}
                        title="Words Read"
                        value={`${stats.readingStats.totalWordsRead.toLocaleString()}`}
                        subtitle="Total words mastered"
                        color={theme.palette.info.main}
                    />
                </Grid>
            </Grid>

            {/* Progress Details */}
            <ProgressChart data={stats.readingStats} />

            {/* Weekly Trends */}
            <Box sx={{ mt: 3 }}>
                <TrendChart weeklyHistory={stats.readingStats?.weeklyHistory} />
            </Box>

            {/* Next Badge to Earn */}
            <NextBadge badges={userBadges} />

            {/* Affiliate Program Section */}
            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
                Affiliate Program
            </Typography>
            <Grid container spacing={3}>
                {renderAffiliateSection()}
            </Grid>

            {/* Analytics Section */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Detailed Analytics
                </Typography>
                <Paper sx={{ mt: 2 }}>
                    <ReadingAnalytics data={readingHistory} />
                </Paper>
            </Box>
        </Container>
    );
};

export default Dashboard;
